import { Language } from "@/hooks/use-browser-language";

export const socialSellingAuditTranslations = {
  pl: {
    // Step indicator
    stepOf: "Krok {step} z 3",
    
    // Step 1
    step1Title: "Audyt Social Selling LinkedIn",
    step1Description: "Sprawdź jak Twoja firma wypada w social sellingu na LinkedIn",
    step1DomainLabel: "Podaj domenę swojej firmy *",
    step1DomainPlaceholder: "np. example.com",
    step1DomainHint: "Podaj tylko domenę, bez http:// i www (np. example.com)",
    step1Feature1Title: "Analiza konkurencji",
    step1Feature1Desc: "Zobacz jak wypadasz na tle rynku",
    step1Feature2Title: "Personal branding",
    step1Feature2Desc: "Ocena profilów pracowników",
    step1Feature3Title: "Rekomendacje",
    step1Feature3Desc: "Konkretne kroki do działania",
    
    // Step 2
    step2Title: "Kto jest Twoją konkurencją?",
    step2Description: "Dodaj domeny konkurencji (opcjonalnie, max 5)",
    step2Placeholder: "np. competitor.com",
    step2AddButton: "Dodaj",
    step2DomainsAdded: "{count}/5 domen dodanych",
    step2Optional: "Ten krok jest opcjonalny - możesz pominąć",
    
    // Step 3
    step3Title: "Dane kontaktowe",
    step3Description: "Uzupełnij swoje dane, aby otrzymać raport",
    step3EmailLabel: "Email firmowy *",
    step3EmailPlaceholder: "nazwa@firma.pl",
    step3PhoneLabel: "Telefon *",
    step3PhonePlaceholder: "+48 123 456 789",
    step3ConsentText: "Wyrażam zgodę na przetwarzanie moich danych osobowych zgodnie z",
    step3PrivacyPolicy: "polityką prywatności",
    
    // Buttons
    next: "Dalej",
    back: "Wstecz",
    submit: "Wyślij zgłoszenie",
    submitting: "Wysyłanie...",
    
    // Validation errors
    errorDomainRequired: "Domena jest wymagana",
    errorDomainInvalid: "Nieprawidłowy format domeny (np. example.com lub example.com.pl)",
    errorEmailRequired: "Email jest wymagany",
    errorEmailInvalid: "Nieprawidłowy adres email",
    errorEmailBusiness: "Proszę podać email firmowy (nie akceptujemy darmowych dostawców)",
    errorPhoneRequired: "Telefon jest wymagany",
    errorPhoneMin: "Numer telefonu musi mieć minimum 9 znaków",
    errorPhoneMax: "Numer telefonu jest za długi",
    errorConsentRequired: "Zgoda jest wymagana",
    
    // Toasts
    toastLimitTitle: "Limit osiągnięty",
    toastLimitDesc: "Możesz dodać maksymalnie 5 domen konkurencji",
    toastInvalidDomainTitle: "Nieprawidłowa domena",
    toastInvalidDomainDesc: "Podaj domenę w formacie: example.com lub example.com.pl",
    toastDuplicateTitle: "Duplikat",
    toastDuplicateDesc: "Ta domena już została dodana",
    toastSuccessTitle: "Zgłoszenie wysłane!",
    toastSuccessDesc: "Nasz zespół przeanalizuje Twoje dane w ciągu 24-48h",
    toastErrorTitle: "Błąd",
    toastErrorDesc: "Wystąpił błąd. Spróbuj ponownie.",
  },
  en: {
    // Step indicator
    stepOf: "Step {step} of 3",
    
    // Step 1
    step1Title: "LinkedIn Social Selling Audit",
    step1Description: "Check how your company performs in social selling on LinkedIn",
    step1DomainLabel: "Enter your company domain *",
    step1DomainPlaceholder: "e.g. example.com",
    step1DomainHint: "Enter only the domain, without http:// and www (e.g. example.com)",
    step1Feature1Title: "Competitor Analysis",
    step1Feature1Desc: "See how you compare to the market",
    step1Feature2Title: "Personal Branding",
    step1Feature2Desc: "Employee profile assessment",
    step1Feature3Title: "Recommendations",
    step1Feature3Desc: "Actionable steps to improve",
    
    // Step 2
    step2Title: "Who are your competitors?",
    step2Description: "Add competitor domains (optional, max 5)",
    step2Placeholder: "e.g. competitor.com",
    step2AddButton: "Add",
    step2DomainsAdded: "{count}/5 domains added",
    step2Optional: "This step is optional - you can skip it",
    
    // Step 3
    step3Title: "Contact Details",
    step3Description: "Fill in your details to receive the report",
    step3EmailLabel: "Business email *",
    step3EmailPlaceholder: "name@company.com",
    step3PhoneLabel: "Phone *",
    step3PhonePlaceholder: "+1 123 456 7890",
    step3ConsentText: "I consent to the processing of my personal data in accordance with the",
    step3PrivacyPolicy: "privacy policy",
    
    // Buttons
    next: "Next",
    back: "Back",
    submit: "Submit Request",
    submitting: "Submitting...",
    
    // Validation errors
    errorDomainRequired: "Domain is required",
    errorDomainInvalid: "Invalid domain format (e.g. example.com or example.co.uk)",
    errorEmailRequired: "Email is required",
    errorEmailInvalid: "Invalid email address",
    errorEmailBusiness: "Please provide a business email (free providers not accepted)",
    errorPhoneRequired: "Phone is required",
    errorPhoneMin: "Phone number must have at least 9 characters",
    errorPhoneMax: "Phone number is too long",
    errorConsentRequired: "Consent is required",
    
    // Toasts
    toastLimitTitle: "Limit reached",
    toastLimitDesc: "You can add a maximum of 5 competitor domains",
    toastInvalidDomainTitle: "Invalid domain",
    toastInvalidDomainDesc: "Enter domain in format: example.com or example.co.uk",
    toastDuplicateTitle: "Duplicate",
    toastDuplicateDesc: "This domain has already been added",
    toastSuccessTitle: "Request submitted!",
    toastSuccessDesc: "Our team will analyze your data within 24-48 hours",
    toastErrorTitle: "Error",
    toastErrorDesc: "An error occurred. Please try again.",
  }
};

export type TranslationKey = keyof typeof socialSellingAuditTranslations.pl;

export function getTranslation(lang: Language, key: TranslationKey, params?: Record<string, string | number>): string {
  let text = socialSellingAuditTranslations[lang][key] || socialSellingAuditTranslations.en[key];
  
  if (params) {
    Object.entries(params).forEach(([paramKey, value]) => {
      text = text.replace(`{${paramKey}}`, String(value));
    });
  }
  
  return text;
}
